import React from 'react';
import { ClipboardList, Sparkles, CalendarCheck, Stethoscope, LineChart } from 'lucide-react';
import { useReveal } from '../hooks/useInView';

const STEPS = [
  {
    icon: ClipboardList,
    title: 'Take the eligibility quiz',
    text: 'A 2-minute medical screening (BMI, history, goals). We auto-flag absolute and high-risk contraindications before anything else.'
  },
  {
    icon: Sparkles,
    title: 'Get matched in seconds',
    text: 'Our AI suggests a personalised plan (1, 2, or 3 bottles) and shortlists licensed clinics based on your location, budget, and outcome ratings.'
  },
  {
    icon: CalendarCheck,
    title: 'Book + pay deposit',
    text: 'Pick a date, secure your slot with a refundable deposit, and receive a QR check-in. Doctor approval is required for high-risk profiles.'
  },
  {
    icon: Stethoscope,
    title: 'Visit your clinic',
    text: 'A licensed MMC-registered doctor reviews your case and administers the injection. You stay in control — same clinic for all follow-ups.'
  },
  {
    icon: LineChart,
    title: 'Track your progress',
    text: 'Weekly weight, photos, and AI insights right in your dashboard. We measure outcomes so you (and we) know what\'s actually working.'
  }
];

function StepCard({ step, index }) {
  const r = useReveal({ threshold: 0.1 });
  const Icon = step.icon;
  return (
    <li
      ref={r.ref}
      className={`${r.className} relative card-soft p-6`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
          <Icon size={20} />
        </div>
        <span className="text-xs font-semibold tracking-widest text-ink-300">
          0{index + 1}
        </span>
      </div>
      <h3 className="mt-5 font-semibold text-ink-900">{step.title}</h3>
      <p className="mt-2 text-sm text-ink-500 leading-relaxed">{step.text}</p>
    </li>
  );
}

export default function HowItWorks() {
  const head = useReveal();
  return (
    <section id="how-it-works" className="section bg-white">
      <div className="container-x">
        <div ref={head.ref} className={`${head.className} max-w-3xl`}>
          <span className="eyebrow">How it works</span>
          <h2 className="h-section mt-4 text-ink-900">
            From quiz to first injection — five guided steps.
          </h2>
          <p className="lead mt-4">
            We removed the guesswork. Take the screening, get a doctor-aligned plan, and
            book with a verified clinic — all in one flow, all auditable.
          </p>
        </div>

        <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {STEPS.map((s, i) => <StepCard key={s.title} step={s} index={i} />)}
        </ol>

        <p className="mt-8 text-xs text-ink-500 max-w-3xl">
          Eligibility logic — Eligible: BMI ≥ 27, or BMI ≥ 25 with metabolic issues.
          Not recommended: BMI &lt; 23 or signs of an eating disorder. Doctor approval
          required for prior pancreatitis, gallstones, severe gastric or kidney issues.
        </p>
      </div>
    </section>
  );
}
